import Bugsnag from '@bugsnag/js'

export type HomepageViewerPhase = 'startup' | 'intro-rotation' | 'interactive'

export interface HomepageViewerDiagnosticState {
  phase: HomepageViewerPhase
  loadedImages: number
  online: boolean
  initialImageReady: boolean
  pageHidden: boolean
}

const getFailedFrame = (error: Error): number | undefined => {
  const match = error.message.match(/\/images\/phone\/iphone-gold-(\d{2})\.png(?:[?\s)]|$)/)
  const frame = match ? Number(match[1]) : 0
  return frame >= 1 && frame <= 72 ? frame : undefined
}

/** Only describe our 72 public frames; never serialize error text or asset URLs. */
export const getHomepageViewerDiagnostics = (error: Error, state: HomepageViewerDiagnosticState) => {
  return {
    schemaVersion: 1,
    phase: state.phase,
    failedFrame: getFailedFrame(error),
    loadedImages: Number.isInteger(state.loadedImages) && state.loadedImages >= 0 && state.loadedImages <= 72
      ? state.loadedImages : undefined,
    online: state.online,
    initialImageReady: state.initialImageReady,
    pageHidden: state.pageHidden,
  }
}

const boundedInteger = (value: number, maximum: number): number | undefined =>
  Number.isInteger(value) && value >= 0 && value <= maximum ? value : undefined

/** Inspect existing public-frame requests only; no probes, URLs or absolute timestamps. */
export const getHomepageViewerResourceDiagnostics = (
  error: Error,
  origin: string,
  timing: Pick<Performance, 'getEntriesByName'>,
) => {
  const frame = getFailedFrame(error)
  if (!frame) return undefined

  try {
    const asset = new URL(`/images/phone/iphone-gold-${String(frame).padStart(2, '0')}.png`, origin)
    const entries = timing.getEntriesByName(asset.href, 'resource') as PerformanceResourceTiming[]
    const requests = entries.filter(entry => entry.initiatorType === 'img' || entry.initiatorType === 'fetch')
      .slice(-4).map(entry => ({
        kind: entry.initiatorType,
        // Zero means unknown/opaque, not HTTP success. Older browsers omit it.
        status: entry.responseStatus === 0 || (entry.responseStatus >= 100 && entry.responseStatus <= 599)
          ? boundedInteger(entry.responseStatus, 599) : undefined,
        transferBytes: boundedInteger(entry.transferSize, 10 * 1024 * 1024),
        bodyBytes: boundedInteger(entry.encodedBodySize, 10 * 1024 * 1024),
        durationMs: Number.isFinite(entry.duration) && entry.duration >= 0 && entry.duration <= 120000
          ? Math.round(entry.duration / 100) * 100 : undefined,
      }))

    return {schemaVersion: 1, availability: requests.length ? 'available' : 'no-entries', requests}
  } catch {
    // Optional browser diagnostics must never prevent the original error report.
    return {schemaVersion: 1, availability: 'unavailable'}
  }
}

export const reportHomepageViewerError = (error: Error, state: HomepageViewerDiagnosticState): void => {
  // The global onError adds route, release and source-asset identity. Keep the
  // original exception/stack, adding only the missing viewer loading context.
  Bugsnag.notify(error, (event) => {
    event.addMetadata('homepageViewer', getHomepageViewerDiagnostics(error, state))
    if (typeof window !== 'undefined') {
      const resources = getHomepageViewerResourceDiagnostics(error, window.location.origin, window.performance)
      if (resources) event.addMetadata('homepageViewerResource', resources)
    }
  })
}
